'use strict';

const libPictProvider = require('pict-provider');

const libMerge = require('./CustomerRecordManifests-Merge.js');

/** The product that owns these ManifestSchema rows (`LIMS-<RecordSet>-<Verb>`), whichever app reads them. */
const MANIFEST_PRODUCT = 'LIMS';

/** The record-set verbs read here. The form verbs (Create / Edit / View) are read by the Sample workspace. */
const RECORD_SET_VERBS =
[
	{ RecordSet: 'Sample', Verb: 'List' },
	{ RecordSet: 'Sample', Verb: 'Filter' },
	{ RecordSet: 'Document', Verb: 'List' },
	{ RecordSet: 'Document', Verb: 'Filter' },
];

const CATALOG_NAMESPACE = 'MasterListCatalog';

/**
 * CustomerRecordManifests — reads the signed-in customer's record-set manifests (ManifestSchema rows
 * `LIMS-<RecordSet>-<Verb>`, pushed with hlrm / hldi) once per customer and layers them into the app:
 *   - List: the master-list registry's columns for that record set (see CustomerRecordManifests-Merge resolveListColumns
 *     for the precedence), so the list, the Samples list columns editor and the cascade all agree;
 *   - Filter: the record set's pict-section-recordset filter schema;
 *   - Document List / Filter: the Documents browser's columns and pick lists (getDocumentBrowseLayout);
 *   - Printable: read by QRCodeProvider.ensurePrintableManifests; this provider only clears it when the customer changes.
 */
class CustomerRecordManifestsProvider extends libPictProvider
{
	/**
	 * @param {any} pFable
	 * @param {any} pOptions
	 * @param {string} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		/** @type {Record<string, any>} manifests by `<RecordSet>-<Verb>`, null where the customer has none */
		this.manifests = {};
		this._loadedCustomerID = null;
		this._loadPromise = null;
		/** @type {WeakMap<any, Record<string, any>>} each record-set provider's own filter schema, before any overlay */
		this._filterBaseByProvider = new WeakMap();
		this._registerTemplateExpressions();
	}

	/** @return {any} the app's REST client */
	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	/** @return {number} the signed-in user's customer, 0 when signed out */
	get customerID()
	{
		const tmpUser = (this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		return Number(tmpUser.CustomerID) || 0;
	}

	/**
	 * The value templates customer List manifests written for legacy LIMS use.
	 */
	_registerTemplateExpressions()
	{
		const tmpMetaTemplate = this.pict.MetaTemplate;
		if (!tmpMetaTemplate || (typeof tmpMetaTemplate.addPattern !== 'function'))
		{
			return;
		}
		const fDate = (pHash, pData, fCallback, pFallBackToCreateDate) =>
		{
			const [ tmpAddress, tmpFormat ] = String(pHash).split('^');
			let tmpDate = this.pict.resolveStateFromAddress(tmpAddress, pData, fCallback);
			if (!tmpDate && pFallBackToCreateDate)
			{
				tmpDate = this.pict.resolveStateFromAddress(`${tmpAddress.split('.').slice(0, -1).join('.')}.CreateDate`, pData, fCallback);
			}
			if (!tmpDate)
			{
				return '';
			}
			const tmpDay = this.pict.fable.Dates.dayJS(tmpDate);
			return tmpDay.isValid() ? tmpDay.format(tmpFormat || 'MM/DD/YYYY') : '';
		};
		tmpMetaTemplate.addPattern('{~DateFormatLIMS:', '~}', (pHash, pData, fCallback) => fDate(pHash, pData, fCallback, true));
		tmpMetaTemplate.addPattern('{~DateFormatNoFallback:', '~}', (pHash, pData, fCallback) => fDate(pHash, pData, fCallback, false));
		tmpMetaTemplate.addPattern('{~ParseSourceLocation:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			const tmpExtension = (tmpRecord && tmpRecord.Extension) || {};
			return formatSourceLocation(tmpExtension.SourceCity, tmpExtension.SourceState, tmpExtension.SourceCountry);
		});
	}

	/**
	 * Read the signed-in customer's manifests, once per customer. A different customer signing in (no reload) reads
	 * afresh and drops the previous customer's QR label layout.
	 * @return {Promise<Record<string, any>>}
	 */
	loadAsync()
	{
		const tmpCustomerID = this.customerID;
		if (this._loadPromise && (this._loadedCustomerID === tmpCustomerID))
		{
			return this._loadPromise;
		}
		if ((this._loadedCustomerID !== null) && (this._loadedCustomerID !== tmpCustomerID))
		{
			this._resetPrintableLayout();
		}
		this._loadedCustomerID = tmpCustomerID;
		this.manifests = {};
		this._loadPromise = tmpCustomerID ? this._fetchAllAsync() : Promise.resolve(this.manifests);
		return this._loadPromise;
	}

	/**
	 * @return {Promise<Record<string, any>>} every RECORD_SET_VERBS manifest, null where the customer has none
	 */
	async _fetchAllAsync()
	{
		const tmpService = this.pict.services.ManifestPackagingService || this.pict.ManifestPackagingService;
		const tmpManifests = {};
		await Promise.all(RECORD_SET_VERBS.map(async (pEntry) =>
		{
			const tmpKey = `${pEntry.RecordSet}-${pEntry.Verb}`;
			tmpManifests[tmpKey] = null;
			if (!tmpService || (typeof tmpService.loadRecordManifestSchema !== 'function'))
			{
				return;
			}
			try
			{
				tmpManifests[tmpKey] = (await tmpService.loadRecordManifestSchema(pEntry.RecordSet, pEntry.Verb, MANIFEST_PRODUCT)) || null;
			}
			catch (pError)
			{
				this.pict.log.error(`CustomerRecordManifests: could not read ${MANIFEST_PRODUCT}-${tmpKey}: ${(pError && pError.message) || pError}`);
			}
		}));
		this.manifests = tmpManifests;
		return tmpManifests;
	}

	/**
	 * @param {string} pRecordSet
	 * @param {string} pVerb
	 * @return {any} the manifest, or null
	 */
	getManifest(pRecordSet, pVerb)
	{
		return this.manifests[`${pRecordSet}-${pVerb}`] || null;
	}

	/**
	 * Drop the QR label layout so the next print reads the signed-in customer's LIMS-<Entity>-Printable rows.
	 */
	_resetPrintableLayout()
	{
		const tmpQR = this.pict.providers.QRCodeProvider;
		if (tmpQR)
		{
			tmpQR.typeConfig = {};
			tmpQR._PrintableManifestsEnsured = false;
		}
	}

	/**
	 * Read one master-list entity's configuration at the Global level alone.
	 * @param {string} pEntity
	 * @return {Promise<Record<string, any>|null>} null when the read fails
	 */
	_getGlobalCatalogEntryAsync(pEntity)
	{
		const tmpRC = this.restClient;
		if (!tmpRC)
		{
			return Promise.resolve(null);
		}
		return new Promise((fResolve) =>
		{
			tmpRC.postJSON('Configuration/Get', { LevelContext: 'Global', LevelContextID: 0, NamespaceKey: CATALOG_NAMESPACE, NamespaceValue: pEntity }, (pError, pBody) =>
			{
				if (pError)
				{
					return fResolve(null);
				}
				return fResolve((pBody && (pBody.Document || pBody)) || {});
			});
		});
	}

	/**
	 * Put each customer List manifest's columns into the master-list registry's entries, beneath any Customer / Project /
	 * User configuration. Called by MasterListRegistry after every cascade load.
	 * @param {Record<string, any>} pEntries - registry entries by entity (modified)
	 * @param {Record<string, any>} pCascadedCatalog - the merged MasterListCatalog namespace, by entity
	 * @param {Record<string, any>} pSeedByEntity - the code seed, by entity
	 * @return {Promise<void>}
	 */
	async overlayMasterListEntriesAsync(pEntries, pCascadedCatalog, pSeedByEntity)
	{
		await this.loadAsync();
		for (const tmpEntry of RECORD_SET_VERBS)
		{
			const tmpEntity = tmpEntry.RecordSet;
			const tmpManifest = (tmpEntry.Verb === 'List') ? this.getManifest(tmpEntity, 'List') : null;
			if (!pEntries[tmpEntity] || !libMerge.hasDescriptors(tmpManifest))
			{
				continue;
			}
			const tmpCascaded = (pCascadedCatalog && pCascadedCatalog[tmpEntity]) || {};
			let tmpGlobal = {};
			if (Array.isArray(tmpCascaded.Columns))
			{
				tmpGlobal = await this._getGlobalCatalogEntryAsync(tmpEntity);
				if (tmpGlobal === null)
				{
					this.pict.log.warn(`CustomerRecordManifests: the Global ${tmpEntity} list configuration could not be read; configured columns win over the customer manifest.`);
					continue;
				}
			}
			const tmpResolved = libMerge.resolveListColumns(
				{
					Manifest: tmpManifest,
					SeedColumns: (pSeedByEntity && pSeedByEntity[tmpEntity] && pSeedByEntity[tmpEntity].Columns) || undefined,
					GlobalColumns: Array.isArray(tmpGlobal.Columns) ? tmpGlobal.Columns : undefined,
					CascadedColumns: tmpCascaded.Columns,
				});
			if (tmpResolved.Source === 'Manifest')
			{
				pEntries[tmpEntity] = Object.assign({}, pEntries[tmpEntity], { Columns: tmpResolved.Columns });
			}
		}
	}

	/**
	 * Layer each customer Filter manifest over its record set's filter schema, restoring the record set's own schema
	 * first (so a customer without a Filter row after one with it gets the platform filters back).
	 * @return {Promise<void>}
	 */
	async applyFilterOverlaysAsync()
	{
		await this.loadAsync();
		for (const tmpEntry of RECORD_SET_VERBS)
		{
			if (tmpEntry.Verb !== 'Filter')
			{
				continue;
			}
			const tmpProvider = this.pict.providers[`RSP-Provider-${tmpEntry.RecordSet}`];
			const tmpManifest = this.getManifest(tmpEntry.RecordSet, 'Filter');
			if (!tmpProvider || ((tmpProvider._FilterSchema === null) || (typeof tmpProvider._FilterSchema !== 'object')))
			{
				continue;
			}
			let tmpBase = this._filterBaseByProvider.get(tmpProvider);
			if (!tmpBase)
			{
				if (!libMerge.hasDescriptors(tmpManifest))
				{
					continue;
				}
				// The schema-derived filters are appended once, when the entity schema first loads; take the base after that.
				try
				{
					if (typeof tmpProvider.getRecordSchema === 'function')
					{
						await tmpProvider.getRecordSchema();
					}
				}
				catch (pError)
				{
					this.pict.log.warn(`CustomerRecordManifests: ${tmpEntry.RecordSet} schema not loaded; its customer filters wait for the next sign-in: ${(pError && pError.message) || pError}`);
					continue;
				}
				if (!tmpProvider._Schema)
				{
					continue;
				}
				tmpBase = JSON.parse(JSON.stringify(tmpProvider._FilterSchema));
				this._filterBaseByProvider.set(tmpProvider, tmpBase);
			}
			const tmpMerged = libMerge.mergeFilterSchema(tmpBase, tmpManifest);
			for (const tmpKey of Object.keys(tmpProvider._FilterSchema))
			{
				delete tmpProvider._FilterSchema[tmpKey];
			}
			Object.assign(tmpProvider._FilterSchema, JSON.parse(JSON.stringify(tmpMerged)));
		}
	}

	/**
	 * The Documents browser's columns and pick lists for the signed-in customer.
	 * @return {Promise<import('./CustomerRecordManifests-Merge.js').DocumentBrowseLayout>}
	 */
	async getDocumentBrowseLayoutAsync()
	{
		await this.loadAsync();
		return libMerge.documentBrowseLayout(this.getManifest('Document', 'List'), this.getManifest('Document', 'Filter'));
	}
}

/**
 * "City, State", or every part with "---" for a missing one when there is a country; the legacy LIMS Source Location.
 * @param {string} [pCity]
 * @param {string} [pState]
 * @param {string} [pCountry]
 * @return {string}
 */
function formatSourceLocation(pCity, pState, pCountry)
{
	if (!pCity && !pState && !pCountry)
	{
		return '';
	}
	if (pCity && pState)
	{
		return `${pCity}, ${pState}`;
	}
	if ((pCity && pCountry) || (pState && pCountry))
	{
		return `${pCity || '---'}, ${pState || '---'}, ${pCountry || '---'}`;
	}
	return pCity || pState || pCountry;
}

module.exports = CustomerRecordManifestsProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'CustomerRecordManifests',
	AutoInitialize: false,
};
module.exports.MANIFEST_PRODUCT = MANIFEST_PRODUCT;
module.exports.RECORD_SET_VERBS = RECORD_SET_VERBS;
module.exports.formatSourceLocation = formatSourceLocation;
